import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useRef,
} from 'react';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  CreateAxiosDefaults,
  isCancel as isAxiosCancel,
  isAxiosError,
} from 'axios';
export {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import {
  DefaultError,
  QueryKey,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

export type HttpDefaults = CreateAxiosDefaults;
export type HttpInstance = AxiosInstance;

const httpContext = createContext<{
  defaults?: HttpDefaults;
  http: HttpInstance;
} | null>(null);

export const isCancel = isAxiosCancel;
export const isHttpError = isAxiosError;

export const HttpProvider: FC<
  PropsWithChildren<{ defaults?: HttpDefaults }>
> = ({ defaults, children }) => {
  const parentHttp = useContext(httpContext);
  const defaultProps = defaults ?? parentHttp?.defaults;
  const httpInstance = useRef(axios.create(defaultProps));

  return (
    <httpContext.Provider
      value={{
        defaults: defaultProps,
        http: httpInstance.current,
      }}
    >
      {children}
    </httpContext.Provider>
  );
};

export const useHttp = () => {
  const context = useContext(httpContext);
  if (!context) {
    throw new Error('use http provider');
  }
  return context.http;
};

type HttpQueryProps<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = {
  queryKey: TQueryKey;
  config: AxiosRequestConfig<TQueryFnData>;
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >;
};

export const useHttpQuery = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  props: HttpQueryProps<TQueryFnData, TError, TData, TQueryKey>
) => {
  const http = useHttp();
  return useQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryKey: props.queryKey,
    queryFn: async () => {
      const response = await http.request<TQueryFnData>(props.config);
      return response.data;
    },
    ...props.options,
  });
};
